<template>
  <div>
    <div v-if="!authReady" class="text-center py-16">
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <login-first v-else-if="!uid" />

    <v-container v-else>
      <v-card outlined class="rounded-lg">
        <v-card-title class="flex-wrap">
          <v-avatar size="40" color="#E3F2F1" class="mr-3">
            <v-icon color="primary" v-text="'mdi-notebook-outline'"></v-icon>
          </v-avatar>
          <div class="mr-4">
            <div class="text-h5 font-weight-bold">Convention</div>
            <div class="caption grey--text text--darken-1">
              Bidding agreements you follow, with the meaning of every
              continuation
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :disabled="!connected"
            @click="openJoinDialog"
          >
            <v-icon left small v-text="'mdi-plus'"></v-icon>
            Add
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
        ></v-progress-linear>

        <div v-if="!loading && conventions.length === 0" class="text-center py-12 px-6">
          <v-icon size="56" color="primary" v-text="'mdi-notebook-outline'"></v-icon>
          <div class="text-h6 mt-3">No convention yet</div>
          <p
            class="body-2 grey--text text--darken-1 mt-2 mx-auto mb-0"
            style="max-width: 460px"
          >
            Add an existing convention by its ID &mdash; try
            <strong>sayc</strong> &mdash; or create your own system and share
            the ID with your partner.
          </p>
          <v-btn
            class="mt-5"
            color="primary"
            text
            :disabled="!connected"
            @click="openJoinDialog"
            v-text="'Add convention'"
          ></v-btn>
        </div>

        <v-container v-else-if="conventions.length > 0">
          <v-row>
            <v-col
              v-for="convention in conventions"
              :key="convention.id"
              cols="12"
              md="6"
            >
              <v-card
                outlined
                class="rounded-lg fill-height convention-card"
                @click="openConvention(convention)"
              >
                <v-card-title class="pb-1">
                  <div class="text-truncate" v-text="convention.name"></div>
                  <v-chip
                    v-if="convention.isAdmin"
                    class="ml-2"
                    x-small
                    label
                    color="primary"
                    text-color="white"
                    v-text="'Admin'"
                  ></v-chip>
                  <v-spacer></v-spacer>
                  <div @click.stop>
                    <v-menu offset-y left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          aria-label="Convention options"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon
                            small
                            v-text="'mdi-dots-vertical'"
                          ></v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="openDetailsDialog(convention)">
                          <v-list-item-icon>
                            <v-icon
                              small
                              v-text="'mdi-pencil-outline'"
                            ></v-icon>
                          </v-list-item-icon>
                          <v-list-item-title
                            v-text="
                              convention.isAdmin
                                ? 'Edit convention'
                                : 'View details'
                            "
                          ></v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </v-card-title>
                <v-card-text class="pt-0">
                  <div
                    class="body-2 grey--text text--darken-1 convention-desc"
                    v-text="convention.desc || 'No description'"
                  ></div>
                </v-card-text>
                <v-card-actions class="pt-0 px-4 pb-3">
                  <span
                    class="caption grey--text"
                    v-text="'ID: ' + convention.id"
                  ></span>
                  <v-spacer></v-spacer>
                  <v-icon
                    small
                    color="primary"
                    v-text="'mdi-arrow-right'"
                  ></v-icon>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <template v-if="!connected">
          <v-divider></v-divider>
          <div class="pa-2 caption grey--text text--darken-1 text-center">
            Checking updates&hellip; changes are disabled until you reconnect.
          </div>
        </template>
      </v-card>
    </v-container>

    <v-dialog v-model="joinDialog" max-width="420" @keydown.enter="joinConvention">
      <v-card class="rounded-lg">
        <v-card-title class="text-h6" v-text="'Add convention'"></v-card-title>
        <v-card-text>
          <v-text-field
            v-model="joinId"
            label="Convention ID"
            prepend-inner-icon="mdi-pound"
            :error-messages="joinError"
            hint="Lowercase letters and numbers, for example sayc"
            persistent-hint
            autofocus
            @input="joinError = ''"
          ></v-text-field>

          <v-divider class="my-4"></v-divider>

          <v-btn text block color="primary" @click="startCreate">
            <v-icon left small v-text="'mdi-plus'"></v-icon>
            Create a new convention
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="joinDialog = false" v-text="'Cancel'"></v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            @click="joinConvention"
            v-text="'Add'"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createDialog" max-width="460" @keydown.enter="createConvention">
      <v-card class="rounded-lg">
        <v-card-title class="text-h6" v-text="'New convention'"></v-card-title>
        <v-card-text>
          <v-text-field
            v-model="createForm.id"
            label="Convention ID"
            prepend-inner-icon="mdi-pound"
            :error-messages="createError"
            hint="Lowercase letters and numbers, for example sayc"
            persistent-hint
            autofocus
            @input="createError = ''"
          ></v-text-field>
          <v-text-field
            v-model="createForm.name"
            class="mt-4"
            label="Name"
            placeholder="SAYC"
          ></v-text-field>
          <v-text-field
            v-model="createForm.desc"
            label="Description"
            placeholder="Standard American Yellow Card"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="createDialog = false" v-text="'Cancel'"></v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            @click="createConvention"
            v-text="'Create'"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="460">
      <v-card v-if="detailsTarget" class="rounded-lg">
        <v-card-title
          class="text-h6"
          v-text="
            detailsTarget.isAdmin ? 'Edit convention' : 'Convention details'
          "
        ></v-card-title>
        <v-card-text>
          <v-text-field
            :value="detailsTarget.id"
            label="Convention ID"
            prepend-inner-icon="mdi-pound"
            disabled
          ></v-text-field>
          <v-text-field
            v-model="detailsForm.name"
            class="mt-4"
            label="Name"
            :disabled="!detailsTarget.isAdmin"
          ></v-text-field>
          <v-text-field
            v-model="detailsForm.desc"
            label="Description"
            :disabled="!detailsTarget.isAdmin"
          ></v-text-field>
          <p
            v-if="!detailsTarget.isAdmin"
            class="caption grey--text text--darken-1 mb-0"
          >
            Only a convention admin can change the name and description.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            color="error"
            @click="askLeave"
            v-text="'Leave convention'"
          ></v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="detailsDialog = false" v-text="'Close'"></v-btn>
          <v-btn
            v-if="detailsTarget.isAdmin"
            color="primary"
            depressed
            :loading="saving"
            @click="saveDetails"
            v-text="'Save'"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="leaveDialog" max-width="400">
      <v-card class="rounded-lg">
        <v-card-title class="text-h6" v-text="'Leave convention?'"></v-card-title>
        <v-card-text>
          <strong v-text="detailsTarget ? detailsTarget.name : ''"></strong>
          will disappear from your list. The convention and its contracts stay
          available to the other members.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="leaveDialog = false" v-text="'Cancel'"></v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            depressed
            :loading="saving"
            @click="leaveConvention"
            v-text="'Leave'"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
      v-text="snackbar.text"
    ></v-snackbar>
  </div>
</template>

<script>
import { db } from "@/App.vue";
import {
  ref,
  onValue,
  get,
  set,
  update,
  remove,
  push,
  runTransaction,
} from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { validateConventionId } from "@/utils/convention";
import LoginFirst from "./LoginFirst.vue";

const BLANK_FORM = () => ({ id: "", name: "", desc: "" });

export default {
  name: "ConventionListVue",

  components: { LoginFirst },

  data: () => ({
    uid: null,
    authReady: false,
    connected: true,
    loading: true,
    roles: {},
    cache: {},
    listeners: {},
    conventions: [],
    stopAuth: null,
    stopConnection: null,
    stopRoles: null,
    joinDialog: false,
    joinId: "",
    joinError: "",
    createDialog: false,
    createForm: BLANK_FORM(),
    createError: "",
    detailsDialog: false,
    detailsTarget: null,
    detailsForm: { name: "", desc: "" },
    leaveDialog: false,
    saving: false,
    snackbar: { show: false, text: "", color: "success" },
  }),

  created() {
    this.stopAuth = onAuthStateChanged(getAuth(), (user) => {
      this.uid = user ? user.uid : null;
      this.authReady = true;
      this.detachConventions();
      if (user) {
        this.loadConventions(user.uid);
      } else {
        this.loading = false;
        this.conventions = [];
      }
    });

    this.stopConnection = onValue(
      ref(db, ".info/connected"),
      (snapshot) => {
        this.connected = snapshot.val() === true;
      }
    );
  },

  beforeDestroy() {
    this.detachConventions();
    if (this.stopAuth) this.stopAuth();
    if (this.stopConnection) this.stopConnection();
  },

  methods: {
    loadConventions(uid) {
      this.loading = true;
      this.stopRoles = onValue(
        ref(db, "users/" + uid + "/conventions"),
        (snapshot) => {
          this.roles = snapshot.val() || {};
          Object.keys(this.roles).forEach((id) => this.listenConvention(id));
          Object.keys(this.listeners).forEach((id) => {
            if (!(id in this.roles)) {
              this.listeners[id]();
              delete this.listeners[id];
              this.$delete(this.cache, id);
            }
          });
          this.refreshList();
        }
      );
    },

    listenConvention(id) {
      if (this.listeners[id]) return;
      this.listeners[id] = onValue(
        ref(db, "conventions/" + id),
        (snapshot) => {
          this.$set(this.cache, id, snapshot.val());
          this.refreshList();
        }
      );
    },

    refreshList() {
      const ids = Object.keys(this.roles);
      const list = [];

      ids.forEach((id) => {
        if (!(id in this.cache)) return;
        const data = this.cache[id];
        if (!data) return;
        list.push({
          id: id,
          name: data.name || id,
          desc: data.desc || "",
          link: data.link || null,
          owner: data.owner || null,
          isAdmin:
            data.owner === this.uid ||
            !!(data.follower && data.follower[this.uid] === "admin"),
        });
      });

      list.sort((a, b) => a.name.localeCompare(b.name));
      this.conventions = list;
      this.loading = ids.some((id) => !(id in this.cache));
    },

    detachConventions() {
      if (this.stopRoles) {
        this.stopRoles();
        this.stopRoles = null;
      }
      Object.keys(this.listeners).forEach((id) => {
        this.listeners[id]();
        delete this.listeners[id];
      });
      this.cache = {};
      this.roles = {};
      this.conventions = [];
    },

    openJoinDialog() {
      this.joinId = "";
      this.joinError = "";
      this.joinDialog = true;
    },

    startCreate() {
      this.createForm = BLANK_FORM();
      this.createForm.id = (this.joinId || "").trim();
      this.createError = "";
      this.joinDialog = false;
      this.createDialog = true;
    },

    async joinConvention() {
      if (!this.uid || this.saving) return;
      const check = validateConventionId(this.joinId);
      if (!check.valid) {
        this.joinError = check.error;
        return;
      }

      this.saving = true;
      try {
        const snapshot = await get(ref(db, "conventions/" + check.id));
        if (!snapshot.exists()) {
          this.joinError = "Not Found";
          return;
        }

        const data = snapshot.val() || {};
        const isOwner = data.owner === this.uid;

        await set(
          ref(db, "users/" + this.uid + "/conventions/" + check.id),
          isOwner ? "owner" : "follower"
        );
        await set(
          ref(db, "conventions/" + check.id + "/follower/" + this.uid),
          isOwner ? "admin" : "follower"
        );

        this.joinDialog = false;
        this.joinId = "";
        this.notify(
          isOwner
            ? "This convention is yours, added with admin rights"
            : "Convention added to your list"
        );
      } catch (error) {
        this.joinError = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    },

    async createConvention() {
      if (!this.uid || this.saving) return;
      const check = validateConventionId(this.createForm.id);
      if (!check.valid) {
        this.createError = check.error;
        return;
      }

      const name =
        (this.createForm.name || "").trim() || check.id.toUpperCase();
      const desc = (this.createForm.desc || "").trim();

      this.saving = true;
      try {
        const result = await runTransaction(
          ref(db, "conventions/" + check.id),
          (data) => {
            if (data !== null) return;
            return { name: name, desc: desc, owner: this.uid };
          }
        );

        if (!result.committed) {
          this.createError = "Already Exist";
          return;
        }

        await set(
          ref(db, "users/" + this.uid + "/conventions/" + check.id),
          "owner"
        );
        await set(
          ref(db, "conventions/" + check.id + "/follower/" + this.uid),
          "admin"
        );

        this.createDialog = false;
        this.createForm = BLANK_FORM();
        this.notify("Convention created");
      } catch (error) {
        this.createError = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    },

    openConvention(convention) {
      this.ensureRootGroup(convention)
        .then(() => {
          this.$router.push({
            name: "Convention Detail",
            params: { id: convention.id },
          });
        })
        .catch((error) => this.notify(this.errorMessage(error), "error"));
    },

    // Mirrors the Android adapter: the root group key is created lazily the
    // first time a convention is opened.
    async ensureRootGroup(convention) {
      if (convention.link) return;
      const newGroup = push(ref(db, "group_contracts"));
      await set(
        ref(db, "conventions/" + convention.id + "/link"),
        newGroup.key
      );
    },

    openDetailsDialog(convention) {
      this.detailsTarget = convention;
      this.detailsForm = { name: convention.name, desc: convention.desc };
      this.detailsDialog = true;
    },

    async saveDetails() {
      if (!this.detailsTarget || !this.detailsTarget.isAdmin || this.saving) {
        return;
      }
      this.saving = true;
      try {
        await update(ref(db, "conventions/" + this.detailsTarget.id), {
          name: (this.detailsForm.name || "").trim(),
          desc: (this.detailsForm.desc || "").trim(),
        });
        this.detailsDialog = false;
        this.notify("Convention updated");
      } catch (error) {
        this.notify(this.errorMessage(error), "error");
      } finally {
        this.saving = false;
      }
    },

    askLeave() {
      this.detailsDialog = false;
      this.leaveDialog = true;
    },

    async leaveConvention() {
      if (!this.detailsTarget || !this.uid || this.saving) return;
      const id = this.detailsTarget.id;
      this.saving = true;
      try {
        await remove(ref(db, "users/" + this.uid + "/conventions/" + id));
        await remove(ref(db, "conventions/" + id + "/follower/" + this.uid));
        this.leaveDialog = false;
        this.detailsTarget = null;
        this.notify("You left the convention");
      } catch (error) {
        this.notify(this.errorMessage(error), "error");
      } finally {
        this.saving = false;
      }
    },

    errorMessage(error) {
      return (error && error.message) || "Something went wrong";
    },

    notify(text, color) {
      this.snackbar = {
        show: true,
        text: text,
        color: color || "success",
      };
    },
  },
};
</script>

<style scoped>
.convention-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.convention-card:hover {
  box-shadow: 0 6px 18px rgba(9, 145, 131, 0.16) !important;
}

.convention-desc {
  min-height: 40px;
}
</style>
